import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { Leaf, Mail, MapPin, Phone } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Thanks for subscribing!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-deep-moss text-white hidden md:block">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4" data-testid="footer-logo">
              <Leaf className="w-6 h-6 text-bone-cream" />
              <span className="font-serif text-xl font-semibold">WEH International</span>
            </Link>
            <p className="text-stone-300 text-sm leading-relaxed font-sans">
              Your global marketplace for organic herbal products, rare exotic plants, and homemade goods.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { name: "Shop All", path: "/shop" },
                { name: "Blog", path: "/blog" },
                { name: "About Us", path: "/about" },
                { name: "Contact", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(" ", "-")}`}
                    className="text-stone-300 hover:text-white text-sm font-sans transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-stone-300 text-sm font-sans">
                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>sales@weh.net</span>
              </li>
              <li className="flex items-start gap-2 text-stone-300 text-sm font-sans">
                <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>(504) 358-3906</span>
              </li>
              <li className="flex items-start gap-2 text-stone-300 text-sm font-sans">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Louisiana, USA</span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-serif text-lg mb-4">Join Our Newsletter</h4>
            <p className="text-stone-300 text-sm font-sans mb-4">
              Be the first to hear about new arrivals and exclusive offers.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="footer-newsletter-input"
                className="bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm font-sans text-white placeholder:text-stone-400 focus:outline-none focus:border-white/40"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-btn"
                className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-4 py-2 text-sm font-sans font-medium transition-colors disabled:opacity-50"
              >
                {loading ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8 text-center">
          <p className="text-stone-400 text-sm font-sans">
            © {new Date().getFullYear()} WEH International. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
